import { useState } from "react";
import { ThumbsUp, ThumbsDown, X } from "@phosphor-icons/react";
import type { Message, MessageFeedbackCreate } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Textarea } from "~/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import rumClient from "~/utils/monitoring";

interface MessageFeedbackInputProps {
  message: Message;
  onSubmit: (messageId: string, feedback: MessageFeedbackCreate) => Promise<void>;
  onClear: (messageId: string) => Promise<void>;
}

export function MessageFeedbackInput({ message, onSubmit, onClear }: MessageFeedbackInputProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openPopover, setOpenPopover] = useState<"up" | "down" | null>(null);
  const [textFeedback, setTextFeedback] = useState("");

  const currentFeedback = message.messageFeedback;
  const currentValue = currentFeedback?.feedback_value;

  const handleThumbClick = (thumbType: "up" | "down") => {
    if (openPopover === thumbType) {
      setOpenPopover(null);
      return;
    }
    setOpenPopover(thumbType);
    setTextFeedback(currentFeedback?.text || "");
  };

  const handleSubmit = async () => {
    if (isSubmitting || !openPopover) return;

    setIsSubmitting(true);
    try {
      const feedbackValue = openPopover === "up" ? 1 : 0;
      const feedback: MessageFeedbackCreate = {
        feedback_type: "BINARY",
        feedback_value: feedbackValue,
        text: textFeedback.trim() || null,
      };

      await onSubmit(message.id, feedback);
      setOpenPopover(null);
      setTextFeedback("");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      rumClient?.recordError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onClear(message.id);
    } catch (error) {
      console.error("Failed to clear feedback:", error);
      rumClient?.recordError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPopoverFormContent = (thumbType: "up" | "down") => {
    const placeholderText =
      thumbType === "up"
        ? "Tell us what you liked about this response"
        : "Tell us what you didn't like about this response";

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // CMD+Enter on Mac or Ctrl+Enter on Windows
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      }
    };

    return (
      <div className="flex flex-col gap-3">
        <div className="font-medium text-sm">Feedback</div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs text-gray-600">Provide Details: (optional)</label>
          <Textarea
            placeholder={placeholderText}
            value={textFeedback}
            onChange={(e) => setTextFeedback(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[80px]"
            disabled={isSubmitting}
          />
        </div>
        <p className="text-xs text-gray-600">
          Your feedback will make this entire conversation visible to administrators.
        </p>
        <Button onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting} size="sm">
          Submit
        </Button>
      </div>
    );
  };

  return (
    <div className="flex items-center gap-1 pl-2 pt-2 mt-2">
      {/* Thumbs Up */}
      <Popover
        open={openPopover === "up"}
        onOpenChange={(open) => {
          if (!open) {
            setOpenPopover(null);
          }
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                onClick={() => handleThumbClick("up")}
                disabled={isSubmitting}
                className={cn(
                  "p-1 rounded transition-colors text-gray-400",
                  "hover:bg-gray-100 cursor-pointer disabled:opacity-50",
                  currentValue === 1 && "text-gray-700"
                )}
                aria-label="Helpful"
                aria-pressed={currentValue === 1}
              >
                <ThumbsUp size={16} weight={currentValue === 1 ? "fill" : "regular"} />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Helpful</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          className="w-80"
          onCloseAutoFocus={(e) => {
            // Prevent focus from returning to the trigger button
            e.preventDefault();
          }}
        >
          {getPopoverFormContent("up")}
        </PopoverContent>
      </Popover>

      {/* Thumbs Down */}
      <Popover
        open={openPopover === "down"}
        onOpenChange={(open) => {
          if (!open) {
            setOpenPopover(null);
          }
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                onClick={() => handleThumbClick("down")}
                disabled={isSubmitting}
                className={cn(
                  "p-1 rounded transition-colors text-gray-400",
                  "hover:bg-gray-100 cursor-pointer disabled:opacity-50",
                  currentValue === 0 && "text-gray-700"
                )}
                aria-label="Not helpful"
                aria-pressed={currentValue === 0}
              >
                <ThumbsDown size={16} weight={currentValue === 0 ? "fill" : "regular"} />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Not helpful</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent
          className="w-80"
          onCloseAutoFocus={(e) => {
            // Prevent focus from returning to the trigger button
            e.preventDefault();
          }}
        >
          {getPopoverFormContent("down")}
        </PopoverContent>
      </Popover>

      {/* Clear button - only show if feedback exists */}
      {currentFeedback && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleClear}
              disabled={isSubmitting}
              className={cn(
                "p-1 rounded transition-colors text-gray-500",
                "hover:bg-gray-100 cursor-pointer disabled:opacity-50"
              )}
              aria-label="Clear feedback"
            >
              <X size={14} />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Clear feedback</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
